import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  Users,
  Target,
  FileText,
  Building2,
  BarChart3,
  Clock,
  Calendar,
  CreditCard,
  TrendingUp,
  Settings,
  ChevronLeft,
  ChevronDown,
  BookOpen,
  PartyPopper,
  GitBranch,
  X,
  ClipboardCheck,
  Inbox,
  Shield,
  ShieldCheck,
  UserCheck,
  Truck,
  ShoppingCart,
  ShoppingBag,
  Quote,
  FileX,
  Receipt,
  Wallet,
  ScanLine,
  BadgeDollarSign,
  Package,
  Landmark,
  Crown,
  IndianRupee,
  User,
  PenLine,
  BookMarked,
  FileSpreadsheet,
  Zap,
  Loader2,
  FlaskConical,
  Activity,
  ClipboardList,
  Database,
  Boxes,
  Warehouse,
  PackageCheck,
  Layers,
  Wrench,
  Flame,
  MapPin,
   ArrowRightLeft,
   RotateCcw,
   Banknote,
   CheckSquare,
   Plug,
   RefreshCw,
   Cpu,
   Upload,
   LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import grx10Logo from "@/assets/grx10-logo.webp";
import grx10Icon from "@/assets/grx10-icon.png";
import { useCurrentRole } from "@/hooks/useRoles";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useUserOrganization } from "@/hooks/useUserOrganization";

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  module?: "financial" | "hrms" | "performance";
}

const navigation: NavItem[] = [
  { name: "Dashboard", path: "/", icon: LayoutDashboard },
];

const financialNav: NavItem[] = [
  { name: "Accounting", path: "/financial/accounting", icon: BookOpen, module: "financial" },
  { name: "Journal Entry", path: "/financial/journal-entry", icon: PenLine, module: "financial" },
  { name: "Ledger Explorer", path: "/financial/ledger", icon: BookMarked, module: "financial" },
  { name: "Customers", path: "/financial/customers", icon: UserCheck, module: "financial" },
  { name: "Vendors", path: "/financial/vendors", icon: Truck, module: "financial" },
  
  { name: "Quotes", path: "/financial/quotes", icon: Quote, module: "financial" },
  { name: "Expenses", path: "/financial/expenses", icon: Wallet, module: "financial" },
  { name: "Bills", path: "/financial/bills", icon: ScanLine, module: "financial" },
  { name: "Reimbursements", path: "/financial/reimbursements", icon: BadgeDollarSign, module: "financial" },
  { name: "Credit Notes", path: "/financial/credit-notes", icon: FileX, module: "financial" },
  { name: "Vendor Credits", path: "/financial/vendor-credits", icon: Receipt, module: "financial" },
  { name: "Banking", path: "/financial/banking", icon: Building2, module: "financial" },
  { name: "Cash Flow", path: "/financial/cashflow", icon: TrendingUp, module: "financial" },
  { name: "Payment Receipts", path: "/financial/payment-receipts", icon: Banknote, module: "financial" },
  { name: "Vendor Payments", path: "/financial/vendor-payments", icon: Banknote, module: "financial" },
  { name: "Exchange Rates", path: "/financial/exchange-rates", icon: ArrowRightLeft, module: "financial" },
  { name: "Assets", path: "/financial/assets", icon: Package, module: "financial" },
  { name: "Statutory Filings", path: "/financial/statutory", icon: Landmark, module: "financial" },
  { name: "E-Way Bills", path: "/financial/eway-bills", icon: Truck, module: "financial" },
  { name: "E-Invoices", path: "/financial/e-invoices", icon: FileText, module: "financial" },
  { name: "Analytics", path: "/financial/analytics", icon: BarChart3, module: "financial" },
  { name: "Recurring", path: "/financial/recurring", icon: RefreshCw, module: "financial" },
  { name: "Automation", path: "/financial/automation", icon: Zap, module: "financial" },
  { name: "Background Jobs", path: "/financial/background-jobs", icon: Loader2, module: "financial" },
  { name: "CA Dashboard", path: "/financial/ca-dashboard", icon: Shield, module: "financial" },
  { name: "CA Audit Console", path: "/financial/audit-console", icon: ShieldCheck, module: "financial" },
];

const hrmsNav: NavItem[] = [
  { name: "Employees", path: "/hrms/employees", icon: Users, module: "hrms" },
  { name: "Attendance", path: "/hrms/attendance", icon: Clock, module: "hrms" },
  { name: "Leaves", path: "/hrms/leaves", icon: Calendar, module: "hrms" },
  { name: "Payroll", path: "/hrms/payroll", icon: CreditCard, module: "hrms" },
  { name: "Holidays", path: "/hrms/holidays", icon: PartyPopper, module: "hrms" },
  { name: "Org Chart", path: "/hrms/org-chart", icon: GitBranch, module: "hrms" },
  { name: "My Profile", path: "/profile", icon: User, module: "hrms" },
  { name: "My Attendance", path: "/hrms/my-attendance", icon: ClipboardCheck, module: "hrms" },
  { name: "My Payslips", path: "/hrms/my-payslips", icon: FileSpreadsheet, module: "hrms" },
  { name: "My Reimbursements", path: "/hrms/reimbursements", icon: BadgeDollarSign, module: "hrms" },
];

// Employee-only HRMS items
const employeeHrmsNav: NavItem[] = [
  { name: "My Profile", path: "/profile", icon: User, module: "hrms" },
  { name: "My Attendance", path: "/hrms/my-attendance", icon: ClipboardCheck, module: "hrms" },
  { name: "Leaves", path: "/hrms/leaves", icon: Calendar, module: "hrms" },
  { name: "My Payslips", path: "/hrms/my-payslips", icon: CreditCard, module: "hrms" },
  { name: "My Reimbursements", path: "/hrms/reimbursements", icon: BadgeDollarSign, module: "hrms" },
];

// Manager HRMS items (same as employee + Inbox)
const managerHrmsNav: NavItem[] = [
  { name: "My Profile", path: "/profile", icon: User, module: "hrms" },
  { name: "My Attendance", path: "/hrms/my-attendance", icon: ClipboardCheck, module: "hrms" },
  { name: "Leaves", path: "/hrms/leaves", icon: Calendar, module: "hrms" },
  { name: "My Payslips", path: "/hrms/my-payslips", icon: CreditCard, module: "hrms" },
  { name: "My Reimbursements", path: "/hrms/reimbursements", icon: BadgeDollarSign, module: "hrms" },
  { name: "Inbox", path: "/hrms/inbox", icon: Inbox, module: "hrms" },
];

// Finance HRMS items (admin views + employee self-service)
const financeHrmsNav: NavItem[] = [
  { name: "Employees", path: "/hrms/employees", icon: Users, module: "hrms" },
  { name: "Payroll", path: "/hrms/payroll", icon: CreditCard, module: "hrms" },
  { name: "CTC Components", path: "/hrms/ctc-components", icon: IndianRupee, module: "hrms" },
  { name: "Org Chart", path: "/hrms/org-chart", icon: GitBranch, module: "hrms" },
  { name: "My Profile", path: "/profile", icon: User, module: "hrms" },
  { name: "My Attendance", path: "/hrms/my-attendance", icon: ClipboardCheck, module: "hrms" },
  { name: "Leaves", path: "/hrms/leaves", icon: Calendar, module: "hrms" },
  { name: "My Payslips", path: "/hrms/my-payslips", icon: CreditCard, module: "hrms" },
  { name: "My Reimbursements", path: "/hrms/reimbursements", icon: BadgeDollarSign, module: "hrms" },
  
];

const inventoryNav: NavItem[] = [
  { name: "Items", path: "/inventory/items", icon: Package, module: "financial" },
  { name: "Warehouses", path: "/inventory/warehouses", icon: Warehouse, module: "financial" },
  { name: "Stock Ledger", path: "/inventory/stock-ledger", icon: BookOpen, module: "financial" },
  { name: "Adjustments", path: "/inventory/adjustments", icon: ClipboardList, module: "financial" },
];

const procurementNav: NavItem[] = [
  { name: "Purchase Orders", path: "/procurement/purchase-orders", icon: ShoppingCart, module: "financial" },
  { name: "Goods Receipts", path: "/procurement/goods-receipts", icon: PackageCheck, module: "financial" },
  { name: "Purchase Returns", path: "/procurement/returns", icon: RotateCcw, module: "financial" },
];

const salesNav: NavItem[] = [
  { name: "Sales Orders", path: "/sales/orders", icon: ShoppingBag, module: "financial" },
  { name: "Deliveries", path: "/sales/deliveries", icon: Truck, module: "financial" },
  { name: "Sales Returns", path: "/sales/returns", icon: RotateCcw, module: "financial" },
  { name: "Sales Invoices", path: "/financial/invoicing", icon: FileText, module: "financial" },
];

const manufacturingNav: NavItem[] = [
  { name: "BOM", path: "/manufacturing/bom", icon: Layers, module: "financial" },
  { name: "Work Orders", path: "/manufacturing/work-orders", icon: Wrench, module: "financial" },
  { name: "Consumption", path: "/manufacturing/consumption", icon: Flame, module: "financial" },
  { name: "Finished Goods", path: "/manufacturing/finished-goods", icon: PackageCheck, module: "financial" },
];

const warehouseNav: NavItem[] = [
  { name: "Bin Locations", path: "/warehouse/bins", icon: MapPin, module: "financial" },
  { name: "Stock Transfers", path: "/warehouse/transfers", icon: ArrowRightLeft, module: "financial" },
  { name: "Picking Lists", path: "/warehouse/picking", icon: ClipboardList, module: "financial" },
  { name: "Inv. Counts", path: "/warehouse/counts", icon: ClipboardCheck, module: "financial" },
];

const performanceNav: NavItem[] = [
  { name: "Goals", path: "/performance/goals", icon: Target, module: "performance" },
  { name: "Memos", path: "/performance/memos", icon: FileText, module: "performance" },
];

let persistedCollapsed = false;
export function getSidebarCollapsed() { return persistedCollapsed; }

// Persist sidebar scroll position across remounts (each route renders its own MainLayout)
let savedSidebarScroll = 0;

// Mobile sidebar state managed via a global-ish export so Header can trigger it
export let mobileMenuOpen = false;
export let setMobileMenuOpen: (v: boolean) => void = () => {};

function NavSection({
  title,
  items,
  sectionId,
  defaultOpen = false,
  collapsed,
  onItemClick,
}: {
  title: string;
  items: NavItem[];
  sectionId: string;
  defaultOpen?: boolean;
  collapsed: boolean;
  onItemClick: () => void;
}) {
  const location = useLocation();
  const hasActiveItem = items.some((item) => location.pathname === item.path);
  const storageKey = `sidebar-section-${sectionId}`;
  const [sectionOpen, setSectionOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) return stored === "true";
    return defaultOpen;
  });

  const toggleSection = () => {
    setSectionOpen((prev) => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      return next;
    });
  };

  const isExpanded = collapsed || hasActiveItem || sectionOpen;

  if (items.length === 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mb-4">
        {/* Full sidebar: clickable section header */}
        {!collapsed && (
          <button
            onClick={toggleSection}
            className="w-full flex items-center justify-between px-3 py-1.5 mb-1 rounded-lg text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50 hover:text-sidebar-foreground/80 hover:bg-sidebar-accent/50 transition-all duration-200 group"
          >
            <span>{title}</span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                isExpanded ? "rotate-0" : "-rotate-90"
              )}
            />
          </button>
        )}

        {/* Mini sidebar: subtle divider with tooltip on hover */}
        {collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mx-auto mb-2 mt-1 h-px w-8 rounded-full bg-sidebar-foreground/20 cursor-default" />
            </TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col gap-0.5 py-2">
              <span className="font-semibold text-xs uppercase tracking-wider">{title}</span>
              <span className="text-xs text-muted-foreground">
                {items.length} item{items.length !== 1 ? "s" : ""}
              </span>
            </TooltipContent>
          </Tooltip>
        )}

        {isExpanded && (
          <nav className="space-y-1">
              {items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;

                const linkContent = (
                  <>
                    {isActive && (
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary to-primary/80 shadow-lg transition-all duration-200" />
                    )}
                    {!isActive && (
                      <div className="absolute inset-0 rounded-xl bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    )}
                    <div className="relative z-10 transition-transform duration-200 group-hover:scale-110">
                      <Icon className={cn("h-5 w-5 flex-shrink-0", collapsed && "mx-auto")} />
                    </div>
                    {!collapsed && (
                      <span className="relative z-10 whitespace-nowrap">{item.name}</span>
                    )}
                    {isActive && !collapsed && (
                      <div className="absolute right-2 h-2 w-2 rounded-full bg-white/80 animate-scale-in" />
                    )}
                  </>
                );

                return collapsed ? (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.path}
                        onClick={onItemClick}
                        className={cn(
                          "group relative flex items-center justify-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                          isActive
                            ? "text-sidebar-primary-foreground"
                            : "text-sidebar-foreground/80 hover:text-sidebar-accent-foreground"
                        )}
                      >
                        {linkContent}
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.name}</TooltipContent>
                  </Tooltip>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onItemClick}
                    className={cn(
                      "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                      isActive
                        ? "text-sidebar-primary-foreground"
                        : "text-sidebar-foreground/80 hover:text-sidebar-accent-foreground"
                    )}
                  >
                    {linkContent}
                  </NavLink>
                );
              })}
          </nav>
        )}
      </div>
    </TooltipProvider>
  );
}


export function Sidebar() {
  const [collapsed, setCollapsed] = useState(persistedCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { data: currentRole } = useCurrentRole();
  const { data: isSuperAdmin, isLoading: superAdminLoading } = useIsSuperAdmin();
  const { signOut } = useAuth();
  const { isModuleEnabled } = useModuleAccess();
  const { data: orgData, isLoading: orgLoading } = useUserOrganization();
  const sidebarScrollRef = useRef<HTMLDivElement>(null);

  // Never blank the navigation while role/org queries settle.
  // Fall back to a safe self-service role until the org-scoped role query resolves.
  const rolesStillLoading = superAdminLoading || orgLoading || currentRole === null;
  const effectiveRole = isSuperAdmin ? "admin" : currentRole ?? "employee";
  const showingFallbackRole = !isSuperAdmin && rolesStillLoading;

  // Restore sidebar scroll position after remount AND after content has rendered
  // We depend on org loading so scroll is restored once the shell is laid out
  useEffect(() => {
    const el = sidebarScrollRef.current;
    if (!el || orgLoading) return;
    // Use rAF to ensure the browser has laid out the content before restoring scroll
    const raf = requestAnimationFrame(() => {
      el.scrollTop = savedSidebarScroll;
    });
    const onScroll = () => { savedSidebarScroll = el.scrollTop; };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
    };
  }, [orgLoading]);

  const isEmployee = effectiveRole === "employee";
  const isManager = effectiveRole === "manager";
  const isFinance = effectiveRole === "finance";
  const isHR = effectiveRole === "hr";
  const isAdmin = effectiveRole === "admin";

  // Expose setter so Header can trigger it
  setMobileMenuOpen = setMobileOpen;

  const handleToggle = () => {
    persistedCollapsed = !collapsed;
    setCollapsed(!collapsed);
    // Dispatch a custom event so MainLayout can react
    window.dispatchEvent(new CustomEvent("sidebar-toggle", { detail: { collapsed: !collapsed } }));
  };

  const closeMobile = () => setMobileOpen(false);


  // Build visible nav based on role and module access.
  // Admins (org admin + super_admin via effectiveRole) always see all modules
  // regardless of the subscription's enabled_modules list — subscriptions scope
  // what non-admin tenants can use, not what their own admin can configure.
  const restrictedRole = isEmployee || isManager || isFinance || isHR;
  const visibleMainNav = restrictedRole ? [] : navigation;
  const visibleFinancialNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? financialNav
    : [];
  const visibleInventoryNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? inventoryNav
    : [];
  const visibleProcurementNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? procurementNav
    : [];
  const visibleSalesNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? salesNav
    : [];
  const visibleManufacturingNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? manufacturingNav
    : [];
  const visibleWarehouseNav = (isEmployee || isManager || isHR) ? []
    : (isAdmin || isModuleEnabled("financial")) ? warehouseNav
    : [];
  const visibleHrmsNav = (!isAdmin && !isModuleEnabled("hrms")) ? []
    : isManager
    ? managerHrmsNav
    : isEmployee
    ? employeeHrmsNav
    : isFinance
    ? financeHrmsNav
    : hrmsNav;
  const visiblePerformanceNav = (isAdmin || isModuleEnabled("performance")) ? performanceNav : [];

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <div className="flex items-center gap-3 overflow-hidden">
          {collapsed ? (
            <img
              src={grx10Icon}
              alt="GRX10"
              className="h-8 w-8 mx-auto transition-all duration-300 rounded"
            />
          ) : (
            <img
              src={grx10Logo}
              alt="GRX10"
              className="h-8 w-auto transition-all duration-300"
            />
          )}
        </div>
        {/* Close button on mobile */}
        <button
          onClick={closeMobile}
          className="md:hidden flex items-center justify-center h-8 w-8 rounded-lg text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <div ref={sidebarScrollRef} className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
        {showingFallbackRole && !collapsed && (
          <div className="mb-4 rounded-xl border border-sidebar-border/60 bg-sidebar-accent/30 px-3 py-2 text-xs text-sidebar-foreground/60">
            Syncing workspace access… showing self-service navigation.
          </div>
        )}

        {visibleMainNav.length > 0 && (
          <NavSection title="Main" items={visibleMainNav} sectionId="main" defaultOpen collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleFinancialNav.length > 0 && (
          <NavSection title="Financial Suite" items={visibleFinancialNav} sectionId="financial" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleInventoryNav.length > 0 && (
          <NavSection title="Inventory" items={visibleInventoryNav} sectionId="inventory" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleProcurementNav.length > 0 && (
          <NavSection title="Procurement" items={visibleProcurementNav} sectionId="procurement" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleSalesNav.length > 0 && (
          <NavSection title="Sales" items={visibleSalesNav} sectionId="sales" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleManufacturingNav.length > 0 && (
          <NavSection title="Manufacturing" items={visibleManufacturingNav} sectionId="manufacturing" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {visibleWarehouseNav.length > 0 && (
          <NavSection title="Warehouse" items={visibleWarehouseNav} sectionId="warehouse" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        <NavSection title="HRMS" items={visibleHrmsNav} sectionId="hrms" collapsed={collapsed} onItemClick={closeMobile} />
        {visiblePerformanceNav.length > 0 && (
          <NavSection title="Performance OS" items={visiblePerformanceNav} sectionId="performance" collapsed={collapsed} onItemClick={closeMobile} />
        )}
        {(isAdmin || isSuperAdmin) && (
          <NavSection
            title="Admin"
            items={[
              { name: "Audit Log", path: "/admin/audit-log", icon: Shield },
              { name: "Approvals", path: "/admin/approvals", icon: CheckSquare },
              { name: "MCP Tools", path: "/admin/mcp-tools", icon: Cpu },
            ]}
            sectionId="admin"
            collapsed={collapsed}
            onItemClick={closeMobile}
          />
        )}
        {(isAdmin || isSuperAdmin) && (
          <NavSection
            title="Connectors"
            items={[
              { name: "Connectors", path: "/connectors", icon: Plug },
            ]}
            sectionId="connectors"
            collapsed={collapsed}
            onItemClick={closeMobile}
          />
        )}
        {isSuperAdmin && (
          <NavSection
            title="Platform"
            items={[
              { name: "Tenants", path: "/platform", icon: Building2 },
              { name: "Sandbox Lab", path: "/platform/sandbox", icon: FlaskConical },
              { name: "DB Inspector", path: "/platform/db-inspector", icon: Database },
              { name: "Audit Log", path: "/platform/audit", icon: ClipboardList },
            ]}
            sectionId="platform"
            collapsed={collapsed}
            onItemClick={closeMobile}
          />
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-sidebar-border p-3">
        {(isAdmin || isSuperAdmin) && (
          <NavLink
            to="/settings"
            onClick={closeMobile}
            className={cn(
              "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
              location.pathname === "/settings"
                ? "text-sidebar-primary-foreground"
                : "text-sidebar-foreground/80 hover:text-sidebar-accent-foreground"
            )}
          >
            {location.pathname === "/settings" && (
              <motion.div
                layoutId="settingsActive"
                className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary to-primary/80 shadow-lg transition-all duration-200"
                initial={false}
              />
            )}
            {location.pathname !== "/settings" && (
              <div className="absolute inset-0 rounded-xl bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            )}
            <div className="relative z-10 transition-transform duration-300 group-hover:rotate-90">
              <Settings className={cn("h-5 w-5 flex-shrink-0", collapsed && "mx-auto")} />
            </div>
            {!collapsed && <span className="relative z-10">Settings</span>}
            {location.pathname === "/settings" && !collapsed && (
              <div className="absolute right-2 h-2 w-2 rounded-full bg-white/80 animate-scale-in" />
            )}
          </NavLink>
        )}

        {/* Sign out — visible to ALL roles */}
        <button
          onClick={async () => {
            closeMobile();
            try {
              await signOut();
            } finally {
              window.location.replace("/auth");
            }
          }}
          className={cn(
            "group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
            "text-sidebar-foreground/60 hover:text-destructive hover:bg-destructive/10"
          )}
        >
          <LogOut className={cn("h-5 w-5 flex-shrink-0", collapsed && "mx-auto")} />
          {!collapsed && <span>Sign out</span>}
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={handleToggle}
          className="mt-2 hidden md:flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm text-sidebar-foreground/50 transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <ChevronLeft
            className={cn("h-4 w-4 transition-transform duration-300", collapsed && "rotate-180")}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>

      {/* Decorative Glow */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary/5 to-transparent pointer-events-none" />
    </>
  );

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 hidden md:flex h-screen flex-col border-r border-sidebar-border transition-[width] duration-300 ease-in-out",
          collapsed ? "w-16" : "w-64"
        )}
        style={{
          background: "linear-gradient(180deg, hsl(270 10% 7%) 0%, hsl(270 10% 5%) 100%)",
        }}
      >
        {sidebarContent}
      </aside>

      {/* ── Mobile Backdrop ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={closeMobile}
          />
        )}
      </AnimatePresence>

      {/* ── Mobile Drawer ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            key="mobile-sidebar"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed left-0 top-0 z-50 flex h-screen w-72 flex-col border-r border-sidebar-border md:hidden"
            style={{
              background: "linear-gradient(180deg, hsl(270 10% 7%) 0%, hsl(270 10% 5%) 100%)",
            }}
          >
            {sidebarContent}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

// Exported hook-style trigger for Header to use
export function useMobileMenu() {
  return { openMobileMenu: () => setMobileMenuOpen(true) };
}
